import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import api from "../container/helper/Api";
import { getRazorpayCheckoutKey } from "../container/helper/razorpay";
import { createFailedOrder } from "../container/redux/paymentSlice";
import { DNA } from "react-loader-spinner";

const PaymentPopup = ({ courseDetails, onClose, onProceedToPayment, isAuthenticated }) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const profileName = useSelector((state) => state.user?.user?.name);
  const [fullName, setFullName] = useState(profileName || "");
  const [nameError, setNameError] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [gstNumber, setGstNumber] = useState("");

  const payment = courseDetails?.price + courseDetails?.price * 0.18;

  const handleRazorpayPayment = async (billingDetails) => {
    try {
      setLoading(true);
      const paymentResponse = await api.proccedPayment({
        grandTotal: payment,
        currency: "INR",
      });

      const { order, razorpayKeyId } = paymentResponse.data.data;

      const options = {
        key: getRazorpayCheckoutKey({ razorpayKeyId }),
        amount: order?.amount,
        currency: order?.currency,
        order_id: order?.id,
        handler: async function (response) {
          try {
            const {
              razorpay_payment_id,
              razorpay_order_id,
              razorpay_signature,
            } = response;

            await api.paymentStepTwo({
              razorpay_order_id,
              razorpay_payment_id,
              razorpay_signature,
              orderId: razorpay_order_id,
              courseId: id,
              totalAmount: payment,
              discount: 0,
              currency: "INR",
              payment_method: "Prepaid",
              tax: 18,
              fullName: billingDetails.fullName,
              businessName: billingDetails.businessName,
              gstNumber: billingDetails.gstNumber || "",
            });

            onClose();
            navigate(`/courses/Courses/CourseDetails/CoursesVideos/${id}`);
            setLoading(false);
          } catch (error) {
            console.error("Error during post-payment processing:", error);
            dispatch(createFailedOrder({ courseId: id, tax: 18 }));
            onClose();
            navigate(`/courses/Courses/CourseDetails/PaymentFailed/${id}`);
            setLoading(false);
          }
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on("payment.failed", function () {
        dispatch(createFailedOrder({ courseId: id, tax: 18 }));
        onClose();
        navigate(`/courses/Courses/CourseDetails/PaymentFailed/${id}`);
      });

      razorpay.open();
      setLoading(false);
    } catch (error) {
      console.error("Payment error:", error);
      dispatch(createFailedOrder({ courseId: id, tax: 18 }));
      onClose();
      navigate(`/courses/Courses/CourseDetails/PaymentFailed/${id}`);
      setLoading(false);
    }
  };

  const handleProceedClick = () => {
    if (isAuthenticated) {
      setStep(2);
    } else {
      onProceedToPayment();
    }
  };

  const handlePayNow = () => {
    if (!fullName.trim()) {
      setNameError("Full name is required");
      return;
    }
    handleRazorpayPayment({
      fullName: fullName.trim(),
      businessName: businessName.trim(),
      gstNumber: gstNumber.trim(),
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[1000]">
      <div className="relative bg-white rounded-xl p-6 w-[90%] max-w-md shadow-2xl">
        <button
          className="absolute top-3 right-3 text-gray-500 text-2xl hover:text-gray-700"
          onClick={onClose}
        >
          ×
        </button>

        {step === 1 ? (
          <>
            <h2 className="text-xl font-semibold text-center mb-4">
              Complete Your Enrollment
            </h2>

            <div className="flex items-center gap-4 mb-4">
              <img
                src={courseDetails?.thumbnail?.[0]}
                alt="course"
                className="w-24 h-16 object-cover rounded-lg"
              />
              <div>
                <h3 className="text-lg font-medium">{courseDetails?.title}</h3>
                <p className="text-gray-600 text-sm">
                  Rs. {courseDetails?.price} + 18% GST
                </p>
                <p className="font-bold text-blue-600 text-lg">
                  Total: Rs. {payment.toFixed(2)}
                </p>
              </div>
            </div>

            {!isAuthenticated && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  Please login to proceed with payment
                </p>
              </div>
            )}

            {loading ? (
              <div className="flex flex-col items-center justify-center py-6">
                <DNA visible={true} height="60" width="60" />
                <p className="mt-2 text-gray-600">Processing payment...</p>
              </div>
            ) : (
              <div className="flex justify-between mt-6">
                <button
                  className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium"
                  onClick={handleProceedClick}
                >
                  {isAuthenticated ? "Proceed to Payment" : "Login to Pay"}
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <h2 className="text-xl font-semibold text-center mb-4">
              Billing Details
            </h2>
            <p className="text-sm text-gray-600 text-center mb-5">
              Enter your billing details for the invoice
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => {
                  setFullName(e.target.value);
                  if (nameError) setNameError("");
                }}
                maxLength={60}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${nameError ? "border-red-500" : "border-gray-300"}`}
              />
              {nameError && <p className="text-xs text-red-500 mt-1">{nameError}</p>}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business Name (If any)
              </label>
              <input
                type="text"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="Optional"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                GST Number
              </label>
              <input
                type="text"
                value={gstNumber}
                onChange={(e) => setGstNumber(e.target.value)}
                placeholder="Optional"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-6">
                <DNA visible={true} height="60" width="60" />
                <p className="mt-2 text-gray-600">Opening payment...</p>
              </div>
            ) : (
              <div className="flex justify-between mt-6">
                <button
                  className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium"
                  onClick={() => setStep(1)}
                >
                  Back
                </button>
                <button
                  className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium"
                  onClick={handlePayNow}
                >
                  Pay Now
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentPopup;
